// React Compiler opt-out: this file still reads mutable external state during render
// (legacy store object getters and/or current-time reads) that the useFluxStore bridge
// doesn't cover. Remove after migrating those reads (see flux-bridge-conversion skill).
'use no memo';

import React, {useEffect, useRef, useState} from 'react';

import {Banner, Button, Icon, Text, WithTooltip} from '@albert-io/atomic';
import type {ClassroomModelV1} from '@albert-io/models';
import {renderMoment} from 'lib/timeUtils';

import {isAutoArchivingSoon} from '../../ClassDetail/AutoArchiveChip';
import {AUTO_ARCHIVE_DETAIL_COPY} from '../../ClassDetail/autoArchiveCopy';
import {useTeacherClassrooms} from '../../hooks/useTeacherClassrooms';
import {getAutoArchiveSeasonBounds, isAutoArchiveSeason} from '../autoArchiveSeason';

/**
 * Whether a class was auto-archived in THIS season — its auto-archive date (always July 1, per mandark)
 * falls in the current window. Excludes classes archived in prior years.
 */
const wasAutoArchivedThisSeason = (classroom: ClassroomModelV1): boolean => {
  const date = classroom.getAutoArchiveDate?.();
  if (!date) {
    return false;
  }
  const {start, end} = getAutoArchiveSeasonBounds();
  // auto_archive_date is a calendar date mandark serializes as UTC midnight (e.g. 2026-07-01T00:00:00Z).
  // Compare it as a UTC calendar day against the season window
  const archiveDay = date.clone().utc().format('YYYY-MM-DD');
  return start.format('YYYY-MM-DD') <= archiveDay && archiveDay <= end.format('YYYY-MM-DD');
};

export const AutoArchiveBanner = () => {
  const {archivedClassrooms, classrooms} = useTeacherClassrooms();
  const [expanded, setExpanded] = useState(false);
  const detailRef = useRef<HTMLSpanElement>(null);

  // Focus the copy after expand commits: a synchronous focus() in a ref callback fires the toggle's
  // blur → tooltip state update mid-commit. The toggle stays mounted, so collapse never drops focus.
  /* eslint-disable react-you-might-not-need-an-effect/no-event-handler -- focus targets a node that only exists after the expanded render commits */
  useEffect(() => {
    if (expanded) {
      detailRef.current?.focus();
    }
  }, [expanded]);
  /* eslint-enable react-you-might-not-need-an-effect/no-event-handler */

  // The season's archived list is loaded up-front by the Home container so this notice renders with
  // the rest of the page instead of popping in a few seconds late (#19169).
  const season = isAutoArchiveSeason();

  const soonest = season
    ? null
    : classrooms
        .filter((classroom) => isAutoArchivingSoon(classroom))
        .minBy((classroom) => classroom.getAutoArchiveDate().valueOf());

  // During the season, only count classes auto-archived in THIS run — not ones archived in prior years.
  const hasArchivedThisSeason =
    season && archivedClassrooms.some((c) => wasAutoArchivedThisSeason(c));

  // Outside the season: show only when a class is auto-archiving soon (active list). During the season:
  // show only if the teacher had a class auto-archived this season. Active and archived are separate
  // lists, so the old `classrooms.isEmpty()` check was wrong on both ends.
  if ((!season && !soonest) || (season && !hasArchivedThisSeason)) {
    return null;
  }

  const title = soonest
    ? // Render in UTC: auto_archive_date is a calendar date stored as UTC midnight
      `Classes will be automatically archived on ${renderMoment(
        soonest.getAutoArchiveDate(),
        'dateFormalShortWithFullMonth',
        'UTC'
      )}`
    : 'Classes were automatically archived';

  return (
    <Banner alignContent='center' type={soonest ? 'warning' : 'info'}>
      <div className='gap-x2 flex grow items-center'>
        <Icon color='inherit' icon='box-archive' iconStyle='regular' />
        <div className='flex flex-col items-start'>
          <Text bold color='inherit'>
            {title}
          </Text>
          {expanded && (
            <span
              id='auto-archive-banner-detail'
              ref={detailRef}
              tabIndex={-1}
              className='outline-none'
            >
              <Text color='inherit'>{AUTO_ARCHIVE_DETAIL_COPY}</Text>
            </span>
          )}
        </div>
        <WithTooltip
          content={expanded ? 'Show less info' : 'Show more info'}
          placement='bottom'
          // The tooltip wrapper (not the Button) is the flex child, so it carries the push-right.
          wrapperProps={{className: 'ml-auto shrink-0'}}
        >
          <Button
            aria-expanded={expanded}
            aria-controls={expanded ? 'auto-archive-banner-detail' : undefined}
            aria-label={expanded ? 'Show less info' : 'Show more info'}
            color='inherit'
            variant='text'
            size='s'
            className='gap-x1 hover:background-brand-subtle-hover! flex items-center'
            onClick={() => setExpanded((current) => !current)}
          >
            <Icon color='inherit' icon='info-circle' iconStyle='regular' />
            <Icon color='inherit' icon={expanded ? 'chevron-down' : 'chevron-left'} />
          </Button>
        </WithTooltip>
      </div>
    </Banner>
  );
};
